import { useEffect, useRef, useState } from "react";
import { ChevronRight, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { kidsApi, formatApiError } from "../lib/api";
import { PinInput } from "./KidsDialog";

const ART = { hello: "👋", ask: "❓", debate: "💬", listen: "👂", safe: "🛡️", promise: "🤝" };

export const SafetyIntro = () => {
  const { user, setUser, logout } = useAuth();
  const [scenes, setScenes] = useState([]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [agree, setAgree] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const audio = useRef(null);
  useEffect(() => { kidsApi.intro().then((d) => setScenes(d.scenes)).catch(() => toast.error("Couldn't load the safety video")); }, []);
  useEffect(() => () => audio.current?.pause(), []);

  const play = async (scene) => {
    audio.current?.pause();
    setPlaying(true);
    try {
      const { url } = await kidsApi.introAudio(scene.id);
      audio.current = new Audio(`${process.env.REACT_APP_BACKEND_URL}${url}`);
      audio.current.onended = () => setPlaying(false);
      await audio.current.play();
    } catch {
      setPlaying(false);
    }
  };
  useEffect(() => { if (scenes[i]) play(scenes[i]); }, [i, scenes]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!scenes.length) return <div className="what-intro grid place-items-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  const s = scenes[i];
  const last = i === scenes.length - 1;
  const finish = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setUser(await kidsApi.introComplete(code));
      audio.current?.pause();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div data-testid="safety-intro" className="what-intro">
      <div className="max-w-xl w-full mx-auto px-6">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#38BDF8]">How WHAT? works · {i + 1} of {scenes.length}</p>
        <div key={s.id} className="what-intro-scene">
          <div className="what-intro-art" aria-hidden>{ART[s.id] || "✨"}</div>
          <h2 data-testid="safety-intro-title" className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-6">{s.title}</h2>
          <p data-testid="safety-intro-text" className="text-lg text-slate-200 mt-4 leading-relaxed">{s.text}</p>
        </div>
        <div className="flex items-center gap-3 mt-8">
          <button data-testid="safety-intro-replay" onClick={() => play(s)} className="what-btn-ghost flex items-center gap-2">
            {playing ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />} {playing ? "Talking…" : "Listen again"}
          </button>
          {i > 0 && <button data-testid="safety-intro-back" onClick={() => setI(i - 1)} className="what-btn-ghost">Back</button>}
          {!last && <button data-testid="safety-intro-next" onClick={() => setI(i + 1)} className="what-btn-primary flex items-center gap-1">Next <ChevronRight size={15} /></button>}
        </div>
        {last && (
          <form onSubmit={finish} data-testid="safety-intro-grown-up" className="mt-8 rounded-2xl border border-[#FBBF24]/40 bg-[#FBBF24]/5 p-5 space-y-3">
            <p className="font-semibold text-[#FBBF24]">For the grown-up</p>
            <label className="flex gap-3 text-sm text-slate-200">
              <input type="checkbox" data-testid="safety-intro-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
              I watched this with my child. I'll remind them that the AI will never ask to meet them, because it's an AI, not a human, and that they should tell me if anyone online asks to meet.
            </label>
            <PinInput testid="safety-intro-code" value={code} onChange={setCode} placeholder={user?.role === "student" ? "Parent or teacher code" : "Parent PIN"} />
            <button data-testid="safety-intro-finish" disabled={!agree || code.length < 4 || busy} className="what-btn-primary w-full !py-3 disabled:opacity-40">
              {busy && <Loader2 size={15} className="animate-spin" />} Start using WHAT?
            </button>
          </form>
        )}
        <button data-testid="safety-intro-logout" onClick={logout} className="mt-8 text-xs text-slate-500 hover:text-slate-300 underline">Not you? Log out</button>
      </div>
    </div>
  );
};
